import { SubmitHandler } from "react-hook-form";
import { useNavigate } from "react-router";

import { IOrganization } from "@src/types/api";

import {
    createOrganization,
    deleteOrganization,
    updateOrganization,
} from "@api";

import { alertActions } from "@features/alert/alertSlice";

import { useAppDispatch } from "@hooks/reduxHooks";

import { buildKey, loaderCache } from "@services/loaderCache";

import { useOrganizationsContext } from "@LeagueManagement/Organizations/contexts/OrganizationsContext";
import {
    ILeagueMgmtOrgInfoData,
    UseOrganizationHandlersProps,
} from "@LeagueManagement/Organizations/types/organizations";

const useOrganizationHandlers = ({
    isAddingOrg,
    organization,
}: UseOrganizationHandlersProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { setOrganizations } = useOrganizationsContext();
    const orgsKey = buildKey("organizations", "organizations");
    const cachedOrgs = loaderCache.get<IOrganization[]>(orgsKey);

    const handleDelete = (id: IOrganization["id"]) => {
        deleteOrganization(id)
            .then(response => {
                dispatch(
                    alertActions.success({
                        level: "success",
                        title: response.message,
                        dismissible: true,
                        persist: true,
                    }),
                );

                if (cachedOrgs) {
                    const updatedOrgs = cachedOrgs.filter(
                        org => org.id !== Number(id),
                    );
                    setOrganizations(updatedOrgs);
                    loaderCache.set(orgsKey, updatedOrgs);
                } else {
                    loaderCache.invalidate(orgsKey);
                }
                navigate(`/league-management/organizations`);
            })
            .catch(error => {
                dispatch(
                    alertActions.error({
                        level: "danger",
                        title: "Deleting the organization failed with the following error:",
                        text:
                            error +
                            "\n\nIf the problem persists, please contact the site admin.",
                        dismissible: true,
                    }),
                );
            });
    };

    const handleSubmit: SubmitHandler<ILeagueMgmtOrgInfoData> = async data => {
        const orgPromise = isAddingOrg
            ? createOrganization(data)
            : updateOrganization(organization!.id, data);

        await orgPromise
            .then(response => {
                const alertAction = isAddingOrg ? "created" : "updated";
                const newOrg = response.organization;

                dispatch(
                    alertActions.success({
                        title: `Organization ${alertAction} successfully`,
                        dismissible: true,
                        persist: isAddingOrg ? true : false,
                    }),
                );

                if (isAddingOrg) {
                    if (cachedOrgs && newOrg) {
                        const updatedOrgs = [...cachedOrgs, newOrg];
                        setOrganizations(updatedOrgs);
                        loaderCache.set(orgsKey, updatedOrgs);
                    } else {
                        loaderCache.invalidate(orgsKey);
                    }
                    navigate(
                        `/league-management/organizations/edit/${newOrg.id}`,
                    );
                } else {
                    if (cachedOrgs) {
                        const updatedOrgs = cachedOrgs.map(o =>
                            o.id === Number(response.organization.id)
                                ? response.organization
                                : o,
                        );
                        setOrganizations(updatedOrgs);
                        loaderCache.set(orgsKey, updatedOrgs);
                    } else {
                        loaderCache.invalidate(orgsKey);
                    }
                }
            })
            .catch(e => {
                const alertAction = isAddingOrg ? "creating" : "updating";

                dispatch(
                    alertActions.error({
                        title: `The server encountered a problem ${alertAction} the organization.`,
                        text: e,
                        dismissible: true,
                    }),
                );
            });
    };

    return { handleDelete, handleSubmit };
};

export default useOrganizationHandlers;
